import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import BarChartRoundedIcon from "@mui/icons-material/BarChartRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import PsychologyRoundedIcon from "@mui/icons-material/PsychologyRounded";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";
import { useNavigate } from "react-router-dom";
import {
  applyIntelligenceRecommendation,
  askTrustBud,
  ensureHorizonIntelligenceBrief,
  ensureDailyIntelligenceBrief,
  ensurePlanReview,
  fetchIntelligenceAnalytics,
  fetchPlanReview,
  fetchLearnedIntelligence,
  fetchIntelligenceSummary,
  recordIntelligenceAction,
  updatePersonalMemory,
  updateAppliedIntelligenceChange,
} from "../api/aiIntelligence";
import PendingActionNotice from "../components/common/PendingActionNotice";

const HORIZONS = [
  { key: "analytics", label: "Analytics" },
  { key: "plan_health", label: "Plan Health" },
  { key: "weekly", label: "Weekly Review" },
];

const ASK_NOT_HELPFUL_REASONS = [
  { value: "wrong_context", label: "Wrong context" },
  { value: "too_generic", label: "Too generic" },
  { value: "bad_action", label: "Wrong action" },
];

const INSIGHTS_LOAD_TIMEOUT_MS = 9000;
const INSIGHTS_LOAD_STUCK_MESSAGE =
  "Insights are taking longer than expected. Showing the latest available view.";

function getInsightTitle(insight, fallback = "No insight yet") {
  return insight?.title || insight?.label || fallback;
}

function getInsightBody(insight, fallback = "TrustBud needs a little more activity before this is useful.") {
  return insight?.message || insight?.body || insight?.description || fallback;
}

function getActionLabel(insight, fallback = "Make this easier") {
  return insight?.recommended_action?.label || insight?.cta_label || fallback;
}

function cleanCopy(value) {
  return String(value || "")
    .replace(/\btime backup\b/gi, "keep-alive step")
    .replace(/\bbackup\b/gi, "keep-alive step")
    .replace(/\bfallback\b/gi, "keep-alive step")
    .replace(/\brecommendations\b/gi, "strategies")
    .replace(/\brecommendation\b/gi, "strategy")
    .replace(/\bexperiment to run\b/gi, "try this for a week")
    .replace(/\bcurrent experiment\b/gi, "suggested adjustment")
    .replace(/\blast experiment\b/gi, "last result")
    .replace(/\bexperiments\b/gi, "adjustments")
    .replace(/\bexperiment\b/gi, "adjustment")
    .replace(/\bhypothesis\b/gi, "why this may help")
    .replace(/\bprotect this habit\b/gi, "make this my focus today")
    .replace(/\bprotected habit\b/gi, "today’s move")
    .replace(/\bprotect\b/gi, "focus")
    .replace(/\btoday’s play\b/gi, "right now")
    .replace(/\btoday's play\b/gi, "right now")
    .replace(/\bcurrent play\b/gi, "right now")
    .replace(/\bthe main play worked\b/gi, "that worked")
    .replace(/\bmain play worked\b/gi, "that worked")
    .replace(/\bthat play worked\b/gi, "that worked")
    .replace(/\bcoach’s read\b/gi, "why this matters")
    .replace(/\bcoach's read\b/gi, "why this matters")
    .replace(/\bthe read\b/gi, "what TrustBud noticed")
    .replace(/\bplaybook\b/gi, "what works for you")
    .trim();
}

function usefulLockedCopy(value, fallback, weakPatterns = []) {
  const copy = cleanCopy(value);
  if (!copy) return fallback;
  if (weakPatterns.some((pattern) => pattern.test(copy))) return fallback;
  return copy;
}

function friendlyInsightsError(message) {
  const text = cleanCopy(message);
  const secondsMatch = text.match(/Expected available in (\d+) seconds/i);
  if (/throttled/i.test(text)) {
    const seconds = secondsMatch ? Number(secondsMatch[1]) : 0;
    const minutes = seconds ? Math.max(1, Math.ceil(seconds / 60)) : null;
    return minutes
      ? `Insights are cooling down after recent refreshes. Try again in about ${minutes} minute${minutes === 1 ? "" : "s"}.`
      : "Insights are cooling down after recent refreshes. Try again in a few minutes.";
  }
  return text || "Unable to load Insights.";
}

function intelActionLabel(action = {}) {
  const actionType = String(action.action_type || "");
  const fallback = cleanCopy(action.primary_cta || action.cta || "");
  if (actionType === "try_small_version") {
    if (fallback && /3[- ]?minute|small version|keep-alive|keep it alive/i.test(fallback)) {
      return /today/i.test(fallback) ? fallback : `${fallback} in Today`;
    }
    return "Keep it alive in Today";
  }
  if (actionType === "start_meal_checkin") return "Start meal check-in in Today";
  if (actionType === "choose_planned_time") return "Change planned time in Plan";
  if (actionType === "open_plan") return "Open Plan";
  if (actionType === "open_sprint" || actionType === "start_reset_sprint") return "Open Accountability";
  return fallback || "Open Today";
}

function rateLabel(value) {
  if (typeof value !== "number" || Number.isNaN(value)) return "";
  return `${Math.round(value * 100)}%`;
}

function numberValue(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function percentValue(value) {
  const parsed = numberValue(value, 0);
  if (parsed > 0 && parsed <= 1) return Math.round(parsed * 100);
  return Math.round(parsed);
}

function titleFromKey(value = "") {
  const labels = {
    the_read: "What TrustBud noticed",
    today_read: "What TrustBud noticed",
    what_trustbud_noticed: "What TrustBud noticed",
    what_the_data_says: "What TrustBud noticed",
    next_weeks_play: "What to change next week",
    what_to_change_next_week: "What to change next week",
    what_to_change_next_month: "What to change next month",
    experiment_to_run: "Try this for a week",
    try_this_for_a_week: "Try this for a week",
    goal_momentum: "Goal momentum",
    best_recovery: "Recovery",
  };
  if (labels[value]) return labels[value];
  return String(value || "")
    .split("_")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function evidenceItems(evidence) {
  if (!Array.isArray(evidence)) return [];
  return evidence
    .map((item) => {
      if (typeof item === "string") return { label: "Evidence", value: item };
      if (!item || typeof item !== "object") return null;
      return {
        label: cleanCopy(item.label || item.name || "Evidence"),
        value: cleanCopy(item.value || item.body || item.summary || ""),
      };
    })
    .filter((item) => item?.value);
}

function statusLabel(status, fallback = "") {
  const value = String(status || "").toLowerCase();
  const labels = {
    confirmed: "Confirmed",
    learning: "Still learning",
    observed: "Observed",
    dismissed: "Stopped",
    stopped: "Stopped",
    not_true: "Not true",
    not_working: "Not working",
  };
  return cleanCopy(fallback || labels[value] || "");
}

function formatUsedIn(values = []) {
  if (!Array.isArray(values) || !values.length) return "";
  const labels = {
    today: "Today",
    plan: "Plan",
    accountability: "Accountability",
    communications: "Coach check-ins",
    coach: "Coach check-ins",
  };
  return values.map((item) => labels[item] || titleFromKey(item)).join(", ");
}

function metricDisplay(value, fallback = "0") {
  if (value === null || value === undefined || value === "") return fallback;
  return String(value);
}

function SectionTitle({ title, kicker }) {
  return (
    <Stack spacing={0.3}>
      <Typography variant="h6" sx={{ fontWeight: 850, color: "#13233d" }}>
        {title}
      </Typography>
      {kicker ? (
        <Typography variant="body2" sx={{ color: "#66758b" }}>
          {kicker}
        </Typography>
      ) : null}
    </Stack>
  );
}

function InsightCard({ icon, title, body, children }) {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: "8px",
        p: 2.4,
        border: "1px solid #e6edf5",
        backgroundColor: "#ffffff",
      }}
    >
      <Stack direction="row" spacing={1.2} alignItems="flex-start">
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: "8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "#edf3f9",
            color: "#17345d",
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 850, color: "#13233d" }}>
            {title}
          </Typography>
          <Typography sx={{ mt: 0.8, fontSize: 15, lineHeight: 1.6, color: "#44566f" }}>
            {body}
          </Typography>
          {children ? <Box sx={{ mt: 1.4 }}>{children}</Box> : null}
        </Box>
      </Stack>
    </Paper>
  );
}

function InsightDetailRow({ label, children }) {
  if (!children) return null;
  return (
    <Paper elevation={0} sx={{ mt: 1, p: 1.25, borderRadius: "8px", bgcolor: "#f8fafc", border: "1px solid #e2e8f0" }}>
      <Typography sx={{ fontSize: 11.5, fontWeight: 950, color: "#315b7d", textTransform: "uppercase", letterSpacing: 0 }}>
        {label}
      </Typography>
      <Box sx={{ mt: 0.45, color: "#44566f", fontSize: 14, lineHeight: 1.55 }}>
        {children}
      </Box>
    </Paper>
  );
}

function confidenceText(value) {
  const normalized = String(value || "").toLowerCase();
  if (normalized === "strong") return "Strong evidence";
  if (normalized === "emerging") return "Emerging evidence";
  return "Still learning";
}

function ConsistencyRiskPanel({ risk, onAction, onAsk, actionFallback = "Open Today" }) {
  if (!risk || typeof risk !== "object") return null;
  const title = cleanCopy(risk.title || "Biggest risk");
  const claim = cleanCopy(risk.claim || risk.body || "");
  if (!title && !claim) return null;
  const evidence = evidenceItems(risk.evidence).slice(0, 3);
  const why = cleanCopy(risk.why_it_matters || "");
  const action = risk.recommended_next_action || {};
  const actionLabel = cleanCopy(action.label || action.cta || actionFallback);
  const isExample = Boolean(risk.example || risk.type === "example");
  return (
    <BriefSection title="Biggest Risk" body="">
      <Stack spacing={1.2} sx={{ mt: 1 }}>
        <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
          <Chip size="small" label={isExample ? "Example only" : confidenceText(risk.confidence)} sx={{ fontWeight: 900 }} />
        </Stack>
        <Box>
          <Typography sx={{ fontWeight: 900, color: "#13233d", fontSize: 17 }}>{title}</Typography>
          {claim ? <Typography sx={{ mt: 0.55, color: "#44566f", lineHeight: 1.6 }}>{claim}</Typography> : null}
        </Box>
        {evidence.length ? (
          <Paper elevation={0} sx={{ borderRadius: "8px", border: "1px solid #e4ebf3", bgcolor: "#f8fbff", p: 1.3 }}>
            <Typography sx={{ fontSize: 11.5, fontWeight: 950, color: "#315b7d", textTransform: "uppercase" }}>
              Evidence
            </Typography>
            <Stack spacing={0.75} sx={{ mt: 0.8 }}>
              {evidence.map((item, index) => (
                <Stack key={`${item.label}-${index}`} direction="row" spacing={1.2} justifyContent="space-between">
                  <Typography sx={{ minWidth: 0, color: "#66758b", fontSize: 13, fontWeight: 800 }}>{item.label}</Typography>
                  <Typography sx={{ minWidth: 0, color: "#13233d", fontSize: 13, fontWeight: 850, textAlign: "right" }}>{item.value}</Typography>
                </Stack>
              ))}
            </Stack>
          </Paper>
        ) : null}
        {why ? (
          <Typography sx={{ color: "#44566f", fontSize: 14.5, lineHeight: 1.55 }}>
            <Box component="span" sx={{ fontWeight: 900, color: "#13233d" }}>Why it matters: </Box>
            {why}
          </Typography>
        ) : null}
        <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
          {onAction ? (
            <Button variant="contained" onClick={() => onAction(risk)} sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 900 }}>
              {actionLabel}
            </Button>
          ) : null}
          {onAsk ? (
            <Button variant="outlined" onClick={() => onAsk(risk)} sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 900 }}>
              Ask why
            </Button>
          ) : null}
        </Stack>
      </Stack>
    </BriefSection>
  );
}

function AppliedChangesCard({ changes, busyId, onUndo, onFeedback, onViewPlan }) {
  if (!changes?.length) return null;
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: "8px",
        p: 2.4,
        border: "1px solid #dceee4",
        backgroundColor: "#f8fffb",
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <CheckCircleRoundedIcon sx={{ color: "success.dark" }} />
        <Typography sx={{ fontSize: 18, fontWeight: 850, color: "#13233d" }}>
          What Plus is measuring
        </Typography>
      </Stack>
      <Stack spacing={1.8} sx={{ mt: 1.8 }}>
        {changes.slice(0, 4).map((change) => (
          <Box key={change.id} sx={{ borderTop: "1px solid #dceee4", pt: 1.5 }}>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
              <Typography sx={{ fontWeight: 800, color: "#13233d" }}>
                {change.display_title || change.title || "Support added"}
              </Typography>
              <Chip size="small" label={change.outcome?.label || change.status_label || "Still learning"} sx={{ fontWeight: 800 }} />
            </Stack>
            <Typography sx={{ mt: 0.65, color: "#44566f", lineHeight: 1.55 }}>
              {change.receipt?.what_changed || change.message || "TrustBud added support."}
            </Typography>
            {change.receipt?.what_did_not_change ? (
              <Typography sx={{ mt: 0.35, color: "#44566f", lineHeight: 1.55 }}>
                {change.receipt.what_did_not_change}
              </Typography>
            ) : null}
            {change.receipt?.watching ? (
              <Typography sx={{ mt: 0.35, color: "#44566f", lineHeight: 1.55 }}>
                Watching: {change.receipt.watching}
              </Typography>
            ) : null}
            {change.outcome?.detail ? (
              <Typography sx={{ mt: 0.35, color: "#44566f", lineHeight: 1.55 }}>
                Result so far: {change.outcome.detail}
              </Typography>
            ) : null}
            <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
              {change.receipt?.measurement_starts_at ? (
                <Chip size="small" label={`Measurement started ${new Date(change.receipt.measurement_starts_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}`} />
              ) : null}
              {rateLabel(change.before_completion_rate) ? (
                <Chip size="small" label={`Before ${rateLabel(change.before_completion_rate)}`} />
              ) : null}
              {rateLabel(change.after_completion_rate) ? (
                <Chip size="small" label={`After ${rateLabel(change.after_completion_rate)}`} />
              ) : null}
              {typeof change.completion_rate_delta === "number" ? (
                <Chip
                  size="small"
                  label={`Delta ${change.completion_rate_delta >= 0 ? "+" : ""}${Math.round(change.completion_rate_delta * 100)}%`}
                />
              ) : null}
            </Stack>
            <Stack direction="row" spacing={1} sx={{ mt: 1.25, flexWrap: "wrap" }}>
              <Button size="small" variant="contained" onClick={() => onViewPlan(change)}>
                View in Plan
              </Button>
              {change.status !== "dismissed" ? (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<ReplayRoundedIcon />}
                  disabled={busyId === change.id}
                  onClick={() => onUndo(change.id)}
                >
                  {busyId === change.id ? "Working..." : "Undo"}
                </Button>
              ) : null}
              {change.feedback_prompt ? (
                <>
                  <Button size="small" onClick={() => onFeedback(change.id, "helpful")}>
                    Helpful
                  </Button>
                  <Button size="small" onClick={() => onFeedback(change.id, "too_soon")}>
                    Too soon
                  </Button>
                  <Button size="small" onClick={() => onFeedback(change.id, "not_accurate")}>
                    Not accurate
                  </Button>
                  <Button size="small" onClick={() => onFeedback(change.id, "not_real_problem")}>
                    Not helpful
                  </Button>
                </>
              ) : null}
            </Stack>
          </Box>
        ))}
      </Stack>
    </Paper>
  );
}

function PersonalInsightSection({ insight }) {
  if (!insight) return null;
  return (
    <BriefSection title="What TrustBud noticed" body={cleanCopy(insight.observation)}>
      {insight.title ? (
        <Typography sx={{ mt: 1, fontWeight: 900, color: "#13233d" }}>
          {cleanCopy(insight.title)}
        </Typography>
      ) : null}
      {insight.meaning ? (
        <InsightDetailRow label="Why this matters">
          {cleanCopy(insight.meaning)}
        </InsightDetailRow>
      ) : null}
      {Array.isArray(insight.evidence) && insight.evidence.length ? (
        <InsightDetailRow label="Evidence">
          <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
            {insight.evidence.slice(0, 3).map((item, index) => (
              <Chip key={`${item}-${index}`} size="small" label={cleanCopy(item)} />
            ))}
          </Stack>
        </InsightDetailRow>
      ) : null}
    </BriefSection>
  );
}

function SuggestedAdjustmentSection({ adjustment, onStrategy, applying }) {
  if (!adjustment?.body) return null;
  return (
    <BriefSection title={cleanCopy(adjustment.title || "Suggested adjustment")} body={cleanCopy(adjustment.body)}>
      {adjustment.watching_label ? (
        <InsightDetailRow label="Watching">
          {cleanCopy(adjustment.watching_label)}
        </InsightDetailRow>
      ) : null}
      <InsightDetailRow label="Action">
        <Button variant="outlined" disabled={applying} onClick={() => onStrategy(adjustment)} sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 900 }}>
          {applying ? "Working..." : intelActionLabel(adjustment)}
        </Button>
      </InsightDetailRow>
    </BriefSection>
  );
}

function DailyBriefCard({ brief, personalIntel, onStrategy, applying }) {
  if (!brief) return null;
  const strategy = brief.strategy || {};
  const rightNow = personalIntel?.right_now || strategy;
  const triage = Array.isArray(personalIntel?.your_day) && personalIntel.your_day.length
    ? personalIntel.your_day
    : (Array.isArray(brief.triage) ? brief.triage : []);
  const learned = Array.isArray(brief.learned) ? brief.learned : [];
  const whatWorks = Array.isArray(personalIntel?.what_works_for_you) ? personalIntel.what_works_for_you : [];
  const topInsight = personalIntel?.top_insight || null;
  const adjustment = personalIntel?.suggested_adjustment || null;
  const deeperPatterns = Array.isArray(personalIntel?.deeper_patterns) ? personalIntel.deeper_patterns : [];
  return (
    <Stack spacing={2.2}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, md: 3.2 },
          borderRadius: "10px",
          border: "1px solid #dceee4",
          backgroundColor: "#ffffff",
        }}
      >
        <Stack spacing={1.4}>
          <Stack direction="row" spacing={1.2} alignItems="center">
            <AutoAwesomeRoundedIcon sx={{ color: "#166534" }} />
            <Typography sx={{ fontSize: 13, fontWeight: 900, color: "#166534", textTransform: "uppercase", letterSpacing: 0.6 }}>
              Right now
            </Typography>
            <Chip
              size="small"
              label={brief.source === "ai_cached" ? "Fresh brief" : "Ready"}
              sx={{ fontWeight: 850 }}
            />
          </Stack>
          <Typography variant="h4" sx={{ fontWeight: 900, color: "#13233d" }}>
            {cleanCopy(rightNow?.title || brief.mirror?.title || "TrustBud is still learning.")}
          </Typography>
          <Typography sx={{ maxWidth: 780, color: "#44566f", fontSize: 16, lineHeight: 1.65 }}>
            {cleanCopy(rightNow?.body || brief.mirror?.body || "Start with one check-in today.")}
          </Typography>
          <Paper elevation={0} sx={{ p: 2, borderRadius: "8px", bgcolor: "#fff", border: "1px solid #e6edf5" }}>
            <Typography sx={{ fontSize: 13, fontWeight: 900, color: "#2f5d8f", textTransform: "uppercase", letterSpacing: 0.5 }}>
              Today’s move
            </Typography>
            <Typography sx={{ mt: 0.8, fontSize: 20, fontWeight: 900, color: "#13233d" }}>
              {cleanCopy(rightNow?.title || strategy.title || "Start here")}
            </Typography>
            <Typography sx={{ mt: 0.7, color: "#44566f", lineHeight: 1.6 }}>
              {cleanCopy(rightNow?.body || strategy.body || "Start with one thing and keep the rest light.")}
            </Typography>
            {strategy.why_this_focus ? (
              <Typography sx={{ mt: 0.8, color: "#66758b", fontSize: 14 }}>
                {cleanCopy(strategy.why_this_focus)}
              </Typography>
            ) : null}
            <Button variant="contained" sx={{ mt: 1.5 }} disabled={applying} onClick={() => onStrategy(rightNow)}>
              {applying ? "Working..." : intelActionLabel(rightNow || strategy)}
            </Button>
          </Paper>
        </Stack>
      </Paper>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
        {topInsight ? <PersonalInsightSection insight={topInsight} /> : null}
        {!topInsight ? <BriefSection title={cleanCopy(brief.yesterday?.title || "Yesterday")} body={cleanCopy(brief.yesterday?.body)} /> : null}
        {!topInsight ? <BriefSection title={cleanCopy(brief.whole_plan_read?.title || "Why this matters")} body={cleanCopy(brief.whole_plan_read?.body)}>
          <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
            {brief.whole_plan_read?.working ? <Chip size="small" label={`Working: ${brief.whole_plan_read.working}`} /> : null}
            {brief.whole_plan_read?.slipping ? <Chip size="small" label={`Slipping: ${brief.whole_plan_read.slipping}`} /> : null}
          </Stack>
        </BriefSection> : null}
        {!topInsight ? <BriefSection title={cleanCopy(brief.pattern?.title || "The pattern that matters")} body={cleanCopy(brief.pattern?.body)}>
          {(brief.pattern?.evidence || []).slice(0, 2).map((item, index) => (
            <Typography key={`${item}-${index}`} sx={{ mt: 0.8, color: "#66758b", fontSize: 13 }}>
              {cleanCopy(item)}
            </Typography>
          ))}
        </BriefSection> : null}
        <SuggestedAdjustmentSection adjustment={adjustment} onStrategy={onStrategy} applying={applying} />
        {triage.length ? (
          <BriefSection title="Your day" body="Start here, then keep the rest light.">
            <Stack spacing={1} sx={{ mt: 1 }}>
              {triage.slice(0, 4).map((row, index) => (
                <Box key={`${row.label}-${row.commitment_id || row.habit_id}-${index}`} sx={{ borderTop: index ? "1px solid #eef3f8" : "none", pt: index ? 1 : 0 }}>
                  <Typography sx={{ fontWeight: 900, color: "#13233d" }}>
                    {cleanCopy(row.label)}: {cleanCopy(row.title)}
                  </Typography>
                  <Typography sx={{ color: "#56667a", fontSize: 14 }}>{cleanCopy(row.body)}</Typography>
                </Box>
              ))}
            </Stack>
          </BriefSection>
        ) : null}
        {deeperPatterns.length ? (
          <BriefSection title="Deeper patterns" body="">
            <Stack spacing={1} sx={{ mt: 1 }}>
              {deeperPatterns.slice(0, 2).map((pattern, index) => (
                <Box key={`${pattern.category || "pattern"}-${index}`}>
                  <Typography sx={{ fontWeight: 900, color: "#13233d" }}>{cleanCopy(pattern.title)}</Typography>
                  <Typography sx={{ color: "#56667a", fontSize: 14 }}>{cleanCopy(pattern.body)}</Typography>
                </Box>
              ))}
            </Stack>
          </BriefSection>
        ) : null}
      </Box>

      {whatWorks.length || learned.length || brief.experiment ? (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
          {whatWorks.length || learned.length ? (
            <BriefSection title="What works for you" body="">
              <Stack spacing={1} sx={{ mt: 1 }}>
                {(whatWorks.length ? whatWorks : learned).slice(0, 3).map((memory, index) => (
                  <Box key={memory.id || index}>
                    <Typography sx={{ fontWeight: 900, color: "#13233d" }}>{cleanCopy(memory.title || memory.summary)}</Typography>
                    <Typography sx={{ color: "#56667a", fontSize: 14 }}>{cleanCopy(memory.body || memory.summary)}</Typography>
                  </Box>
                ))}
              </Stack>
            </BriefSection>
          ) : null}
          {brief.experiment ? (
            <BriefSection title={cleanCopy(brief.experiment.title || "Last result")} body={cleanCopy(brief.experiment.body)} />
          ) : null}
        </Box>
      ) : null}
    </Stack>
  );
}

function BriefSection({ title, body, children }) {
  return (
    <Paper elevation={0} sx={{ p: 2.1, borderRadius: "8px", border: "1px solid #e6edf5", bgcolor: "#fff" }}>
      <Typography sx={{ fontSize: 18, fontWeight: 900, color: "#13233d" }}>{cleanCopy(title)}</Typography>
      {body ? <Typography sx={{ mt: 0.8, color: "#44566f", lineHeight: 1.6 }}>{cleanCopy(body)}</Typography> : null}
      {children}
    </Paper>
  );
}

function HorizonSwitch({ active, onChange }) {
  return (
    <Box
      sx={{
        display: "flex",
        gap: 0.7,
        overflowX: "auto",
        pb: 0.3,
        mx: { xs: -0.4, md: 0 },
        px: { xs: 0.4, md: 0 },
      }}
    >
      {HORIZONS.map((item) => {
        const selected = active === item.key;
        return (
          <Button
            key={item.key}
            variant={selected ? "contained" : "outlined"}
            onClick={() => onChange(item.key)}
            sx={{
              borderRadius: "8px",
              px: 1.6,
              py: 0.75,
              flexShrink: 0,
              textTransform: "none",
              fontWeight: 900,
              boxShadow: "none",
              ...(selected
                ? { bgcolor: "#10233f", color: "#ffffff", "&:hover": { bgcolor: "#10233f" } }
                : { color: "#52627a", borderColor: "#d9e3ee", bgcolor: "#ffffff" }),
            }}
          >
            {item.label}
          </Button>
        );
      })}
    </Box>
  );
}

function IntelHero({ eyebrow, title, body, icon = <AutoAwesomeRoundedIcon /> }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2.5, md: 3.2 },
        borderRadius: "10px",
        border: "1px solid #dceee4",
        background: "#10233f",
        color: "#ffffff",
        overflow: "hidden",
      }}
    >
      <Stack direction="row" spacing={1.1} alignItems="center">
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "rgba(255,255,255,0.12)",
          }}
        >
          {React.cloneElement(icon, { sx: { color: "#fff5c4" } })}
        </Box>
        <Typography sx={{ fontSize: 12, fontWeight: 950, letterSpacing: 0.8, textTransform: "uppercase", color: "#d9f99d" }}>
          {eyebrow}
        </Typography>
      </Stack>
      <Typography sx={{ mt: 1.4, fontSize: 30, lineHeight: 1.12, fontWeight: 900, letterSpacing: 0 }}>
        {cleanCopy(title)}
      </Typography>
      <Typography sx={{ mt: 1.1, maxWidth: 820, color: "#d8e3ef", fontSize: 16, lineHeight: 1.65 }}>
        {cleanCopy(body)}
      </Typography>
    </Paper>
  );
}

function MetricTile({ label, value, detail }) {
  return (
    <Paper elevation={0} sx={{ p: 1.35, borderRadius: "8px", border: "1px solid #e4ebf3", bgcolor: "#ffffff" }}>
      <Typography sx={{ color: "#102033", fontSize: 24, lineHeight: 1, fontWeight: 950 }}>
        {metricDisplay(value)}
      </Typography>
      <Typography sx={{ mt: 0.55, color: "#52627a", fontSize: 12, fontWeight: 900, textTransform: "uppercase" }}>
        {cleanCopy(label)}
      </Typography>
      {detail ? <Typography sx={{ mt: 0.55, color: "#66758b", fontSize: 13 }}>{cleanCopy(detail)}</Typography> : null}
    </Paper>
  );
}

function BarList({ data, maxValue }) {
  const max = Math.max(1, maxValue || 0, ...data.map((item) => numberValue(item.value)));
  return (
    <Stack spacing={1.1}>
      {data.map((item) => {
        const value = numberValue(item.value);
        return (
          <Box key={item.label}>
            <Stack direction="row" justifyContent="space-between" spacing={1}>
              <Typography sx={{ color: "#13233d", fontSize: 13, fontWeight: 900 }}>{cleanCopy(item.label)}</Typography>
              <Typography sx={{ color: "#315b7d", fontSize: 13, fontWeight: 900 }}>{metricDisplay(item.value)}</Typography>
            </Stack>
            <Box sx={{ mt: 0.55, height: 8, borderRadius: 4, bgcolor: "#edf2f7", overflow: "hidden" }}>
              <Box
                sx={{
                  width: `${Math.max(4, Math.min(100, (value / max) * 100))}%`,
                  height: "100%",
                  borderRadius: 4,
                  bgcolor: item.color || "#315b7d",
                }}
              />
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
}

function isInactiveAnalyticsHabit(habit = {}) {
  const lifecycle = String(habit.lifecycle_status || habit.status || "").trim().toLowerCase();
  return (
    habit.is_active === false ||
    habit.is_actionable === false ||
    (!!lifecycle && !["active", "ready", "watching", "learning"].includes(lifecycle))
  );
}

function strongestSupportMetric(effect = {}) {
  const metrics = Array.isArray(effect.metrics) ? effect.metrics : [];
  return metrics
    .filter((metric) => metric.status === "ready")
    .sort((a, b) => Math.abs(numberValue(b.delta_points)) - Math.abs(numberValue(a.delta_points)))[0] || null;
}

function analyticsDecisionTitle({ topLeverage, slippingComparison, completionRate, headline }) {
  if (topLeverage?.title) return `Make ${cleanCopy(topLeverage.title)} easier.`;
  if (slippingComparison?.headline) return cleanCopy(slippingComparison.headline);
  if (completionRate >= 70) return "Protect what is already working.";
  return cleanCopy(headline || "Choose one easier next move.");
}

function analyticsDecisionBody({ topLeverage, insight, completionRate, activeDays, windowDays }) {
  if (insight?.action) return cleanCopy(insight.action);
  if (topLeverage?.reason) return cleanCopy(topLeverage.reason);
  if (completionRate >= 70) {
    return "The best move may be changing less. Keep the strongest routine stable while TrustBud watches the next signal.";
  }
  return `${activeDays}/${windowDays} days have usable behavior signal. Keep the next action small enough to repeat.`;
}

function AnalyticsDataRoom({ payload, analytics, learnedPayload, days, onDaysChange, onAskPrompt }) {
  const [activeLens, setActiveLens] = useState("overview");
  const todayItems = Array.isArray(payload?.today_plan_state) ? payload.today_plan_state : [];
  const doneToday = todayItems.filter((item) => ["done", "backup_done", "small_version_done", "meal_checkin_done", "completed"].includes(String(item.status_today || ""))).length;
  const dueToday = todayItems.filter((item) => !["not_due_today", "paused", "can_wait"].includes(String(item.status_today || ""))).length || todayItems.length || 1;
  const memories = Array.isArray(learnedPayload?.memories) ? learnedPayload.memories : [];
  const allHabits = Array.isArray(analytics?.habit_portfolio) ? analytics.habit_portfolio : [];
  const habits = allHabits.filter((habit) => !isInactiveAnalyticsHabit(habit));
  const inactiveTitles = new Set(
    allHabits
      .filter((habit) => habit.title && isInactiveAnalyticsHabit(habit))
      .map((habit) => String(habit.title || "").trim().toLowerCase())
  );
  const inactiveIds = new Set(
    allHabits
      .filter((habit) => habit.id !== undefined && isInactiveAnalyticsHabit(habit))
      .map((habit) => Number(habit.id))
  );
  const rawInsightCards = Array.isArray(analytics?.insight_cards) ? analytics.insight_cards : [];
  const insightCards = rawInsightCards.filter((card) => {
    if (!Array.isArray(card.evidence) || !card.evidence.length) return false;
    const text = `${card.title || ""} ${card.pattern || ""} ${card.body || ""}`.toLowerCase();
    if (!text.includes("highest leverage")) return true;
    for (const title of inactiveTitles) {
      if (title && text.includes(title)) return false;
    }
    return true;
  });
  const hero = analytics?.hero || {};
  const completionRate = percentValue(hero.completion_rate || 0);
  const activeDays = numberValue(hero.active_days);
  const windowDays = numberValue(analytics?.window?.days, days);
  const comparisons = Array.isArray(analytics?.comparisons) ? analytics.comparisons : [];
  const slippingComparison = comparisons.find((comparison) => comparison.direction === "down") || null;
  const leverage = (Array.isArray(analytics?.leverage) ? analytics.leverage : []).filter((item) => {
    const id = Number(item.habit_id || 0);
    const title = String(item.title || "").trim().toLowerCase();
    return (!id || !inactiveIds.has(id)) && (!title || !inactiveTitles.has(title));
  });
  const topLeverage = leverage[0] || null;
  const nextInsight = insightCards.find((card) => cleanCopy(card.action || "")) || insightCards[0] || null;
  const supportEffect = analytics?.accountability?.effect || {};
  const supportMetric = strongestSupportMetric(supportEffect);
  const overviewTitle = analyticsDecisionTitle({ topLeverage, slippingComparison, completionRate, headline: hero.headline });
  const overviewBody = analyticsDecisionBody({ topLeverage, insight: nextInsight, completionRate, activeDays, windowDays });
  const overviewMetrics = [
    { label: "Completion", value: `${completionRate}%`, detail: "scheduled habit days" },
    { label: "Active days", value: `${activeDays}/${windowDays}`, detail: "behavior signal" },
    { label: "Recovery", value: `${percentValue(analytics?.recovery?.comeback_rate || 0)}%`, detail: "bounce-back rate" },
    { label: "Signal", value: `${numberValue(analytics?.signal_confidence?.score)}%`, detail: analytics?.signal_confidence?.label || "confidence" },
  ];
  const goalBars = Array.isArray(analytics?.goal_momentum)
    ? analytics.goal_momentum.slice(0, 5).map((item) => ({ label: item.title || item.goal || "Goal", value: item.completion_rate || item.moved || 0, color: "#2f6f73" }))
    : [];
  const weekdayBars = Array.isArray(analytics?.weekday_profile)
    ? analytics.weekday_profile.map((item) => ({ label: item.label || item.weekday || "Day", value: item.completion_rate ?? item.completions ?? item.done ?? item.value ?? 0, color: "#315b7d" }))
    : [];
  const supportRows = Array.isArray(supportEffect.metrics)
    ? supportEffect.metrics.slice(0, 4).map((metric) => ({
        label: metric.label || metric.primary_label || "Support",
        value: metric.primary_rate || 0,
        color: metric.status === "ready" ? "#2f6f73" : "#94a3b8",
      }))
    : [];
  const decisionItems = [
    {
      label: "Bottleneck",
      title: topLeverage?.title || slippingComparison?.label || "No single habit is driving the read.",
      body: topLeverage?.reason || slippingComparison?.headline || "TrustBud is seeing plan-level signal, so avoid over-correcting one habit yet.",
      action: topLeverage ? "Habits" : "Timing",
      lens: topLeverage ? "habits" : "timing",
    },
    {
      label: "Next move",
      title: nextInsight?.action ? "Try one adjustment this week." : "Keep the next action small.",
      body: nextInsight?.action || (topLeverage?.title ? `Make ${topLeverage.title} easier before adding more pressure.` : "Pick the smallest useful action and repeat it before changing the plan."),
      action: "Ask why",
      prompt: `What is the most useful adjustment in my Insights right now? ${overviewTitle}`,
    },
    {
      label: supportMetric ? "Support signal" : "Keep stable",
      title: supportMetric?.label || supportMetric?.primary_label || (completionRate >= 70 ? "Do not change too much." : "Hold the experiment steady."),
      body: supportMetric?.interpretation || analytics?.signal_confidence?.detail || "TrustBud needs the next few days to compare the same plan, not a different plan every day.",
      action: supportMetric ? "Support" : "What to watch",
      lens: supportMetric ? "support" : undefined,
      prompt: "What should I keep stable while TrustBud keeps learning?",
    },
  ];
  const promptItems = [
    "Which habit is most likely to slip this week?",
    "What pattern is TrustBud most confident about?",
    "Where should I simplify the plan?",
  ];
  const lenses = [
    { key: "overview", label: "Overview" },
    { key: "habits", label: "Habits" },
    { key: "goals", label: "Goals" },
    { key: "timing", label: "Timing" },
    { key: "support", label: "Support" },
  ];

  return (
    <Stack spacing={2.2}>
      <IntelHero
        eyebrow="Behavior read"
        title={overviewTitle}
        body={overviewBody}
        icon={<BarChartRoundedIcon />}
      />
      <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", alignItems: "center" }}>
        {[30, 90, 180].map((item) => (
          <Button key={item} size="small" variant={days === item ? "contained" : "outlined"} onClick={() => onDaysChange(item)} sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 900 }}>
            {item} days
          </Button>
        ))}
        {lenses.map((item) => (
          <Button key={item.key} size="small" variant={activeLens === item.key ? "contained" : "outlined"} onClick={() => setActiveLens(item.key)} sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 900 }}>
            {item.label}
          </Button>
        ))}
      </Stack>

      {activeLens === "overview" ? (
        <>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, gap: 1.4 }}>
            {decisionItems.map((item) => (
              <Paper key={item.label} elevation={0} sx={{ p: 2, borderRadius: "8px", border: "1px solid #e4ebf3", bgcolor: "#fff" }}>
                <Typography sx={{ color: "#64748b", fontSize: 12, fontWeight: 950, textTransform: "uppercase" }}>{item.label}</Typography>
                <Typography sx={{ mt: 0.5, color: "#102033", fontSize: 18, fontWeight: 950 }}>{cleanCopy(item.title)}</Typography>
                <Typography sx={{ mt: 0.7, color: "#52627a", lineHeight: 1.55 }}>{cleanCopy(item.body)}</Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => item.lens ? setActiveLens(item.lens) : onAskPrompt(item.prompt)}
                  sx={{ mt: 1.4, borderRadius: "8px", textTransform: "none", fontWeight: 900 }}
                >
                  {item.action}
                </Button>
              </Paper>
            ))}
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1.1fr 0.9fr" }, gap: 2 }}>
            <Paper elevation={0} sx={{ p: 2.1, borderRadius: "8px", border: "1px solid #e4ebf3", bgcolor: "#fff" }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                <Box>
                  <Typography sx={{ color: "#64748b", fontSize: 12, fontWeight: 950, textTransform: "uppercase" }}>Supporting evidence</Typography>
                  <Typography sx={{ mt: 0.35, color: "#102033", fontSize: 22, fontWeight: 950 }}>{completionRate}% completion</Typography>
                </Box>
                <Chip label={`${metricDisplay(hero.current_streak, 0)} day streak`} sx={{ fontWeight: 900 }} />
              </Stack>
              <LinearProgress variant="determinate" value={Math.max(0, Math.min(100, completionRate))} sx={{ mt: 2, height: 10, borderRadius: 5, bgcolor: "#edf2f7", "& .MuiLinearProgress-bar": { bgcolor: "#2f6f73" } }} />
              <Box sx={{ mt: 2, display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" }, gap: 1 }}>
                {overviewMetrics.map((record, index) => (
                  <MetricTile key={`${record.label}-${index}`} label={record.label} value={record.value} detail={record.detail} />
                ))}
              </Box>
            </Paper>
            <Paper elevation={0} sx={{ p: 2.1, borderRadius: "8px", border: "1px solid #e4ebf3", bgcolor: "#fff" }}>
              <Typography sx={{ color: "#102033", fontSize: 18, fontWeight: 950 }}>Today runway</Typography>
              <Typography sx={{ mt: 0.6, color: "#60738c" }}>{doneToday} done out of {dueToday} due or relevant items.</Typography>
              <LinearProgress variant="determinate" value={Math.max(0, Math.min(100, (doneToday / Math.max(dueToday, 1)) * 100))} sx={{ mt: 1.5, height: 10, borderRadius: 5, bgcolor: "#edf2f7", "& .MuiLinearProgress-bar": { bgcolor: "#22c55e" } }} />
              <Box sx={{ mt: 1.8, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
                <MetricTile label="Saved patterns" value={memories.length} />
                <MetricTile label="Habits watched" value={habits.length} />
              </Box>
            </Paper>
          </Box>
          {insightCards.length ? (
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, gap: 1.4 }}>
              {insightCards.slice(0, 3).map((card, index) => (
                <BriefSection key={`${card.title}-${index}`} title={card.pattern || card.title || "Signal"} body={card.body || card.detail || ""}>
                  {card.action ? <Chip size="small" label={`Next: ${cleanCopy(card.action)}`} sx={{ mt: 1, fontWeight: 850 }} /> : null}
                </BriefSection>
              ))}
            </Box>
          ) : null}
        </>
      ) : null}

      {activeLens === "habits" ? (
        <BriefSection title="Habit performance map" body={habits.length ? "" : "TrustBud needs more completed days before this map is useful."}>
          {habits.length ? (
            <Stack spacing={1.1} sx={{ mt: 1.2 }}>
              {habits.slice(0, 5).map((habit, index) => (
                <Box key={`${habit.id || habit.title}-${index}`} sx={{ p: 1.2, borderRadius: "8px", bgcolor: "#f8fafc", border: "1px solid #e8eef5" }}>
                  <Stack direction="row" spacing={1} justifyContent="space-between">
                    <Typography sx={{ color: "#102033", fontWeight: 900 }}>{cleanCopy(habit.title || "Habit")}</Typography>
                    <Typography sx={{ color: "#315b7d", fontWeight: 950 }}>{percentValue(habit.completion_rate || 0)}%</Typography>
                  </Stack>
                  <Typography sx={{ mt: 0.3, color: "#64748b", fontSize: 13 }}>{cleanCopy(habit.goal || statusLabel(habit.status) || "Habit")}</Typography>
                </Box>
              ))}
            </Stack>
          ) : null}
          {leverage.length ? (
            <Stack spacing={1.1} sx={{ mt: 1.5 }}>
              {leverage.slice(0, 3).map((item) => (
                <Box key={`${item.rank}-${item.habit_id || item.title}`} sx={{ p: 1.2, borderRadius: "8px", bgcolor: "#fff7ed", border: "1px solid #fed7aa" }}>
                  <Typography sx={{ color: "#102033", fontWeight: 950 }}>{cleanCopy(item.title || "Focus candidate")}</Typography>
                  <Typography sx={{ mt: 0.3, color: "#7c2d12", fontSize: 13 }}>{cleanCopy(item.reason || "")}</Typography>
                </Box>
              ))}
            </Stack>
          ) : null}
        </BriefSection>
      ) : null}

      {activeLens === "goals" ? (
        <BriefSection title="Goal momentum" body={goalBars.length ? "" : "Goal momentum appears once TrustBud has enough habit signal."}>
          {goalBars.length ? <Box sx={{ mt: 1.2 }}><BarList data={goalBars} maxValue={100} /></Box> : null}
        </BriefSection>
      ) : null}

      {activeLens === "timing" ? (
        <BriefSection title="Week pattern" body={weekdayBars.length ? "" : "TrustBud is still collecting weekday patterns."}>
          {weekdayBars.length ? <Box sx={{ mt: 1.2 }}><BarList data={weekdayBars} maxValue={100} /></Box> : null}
        </BriefSection>
      ) : null}

      {activeLens === "support" ? (
        <BriefSection title={supportEffect.title || "Accountability effect"} body={supportEffect.body || "TrustBud is learning whether proof, reports, replies, and groups change follow-through."}>
          {supportRows.length ? <Box sx={{ mt: 1.2 }}><BarList data={supportRows} maxValue={100} /></Box> : null}
          {supportEffect.recommendation?.body ? (
            <Typography sx={{ mt: 1.4, color: "#315b7d", fontWeight: 850 }}>{cleanCopy(supportEffect.recommendation.body)}</Typography>
          ) : null}
        </BriefSection>
      ) : null}

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr" }, gap: 2 }}>
        <BriefSection title="Analyst prompts" body="Use these to turn the dashboard into a practical next question.">
          <Stack direction="row" spacing={1} sx={{ mt: 1.2, flexWrap: "wrap" }}>
            {promptItems.map((prompt) => (
              <Button key={prompt} size="small" variant="outlined" onClick={() => onAskPrompt(prompt)} sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 850 }}>
                {prompt}
              </Button>
            ))}
          </Stack>
        </BriefSection>
      </Box>
    </Stack>
  );
}

function PeriodBriefView({ brief, onPrimary }) {
  if (!brief) {
    return <BriefSection title="No read yet" body="TrustBud needs a little more activity before this period read is useful." />;
  }
  const horizon = String(brief.horizon || "weekly");
  const sections = brief.sections || {};
  const orderedKeys =
    horizon === "monthly"
      ? ["what_trustbud_noticed", "what_the_data_says", "what_changed", "goal_momentum", "what_to_change_next_month", "try_this_for_a_week"]
      : ["what_trustbud_noticed", "the_read", "what_worked", "what_slipped", "what_changed", "best_recovery", "what_to_change_next_week", "next_weeks_play", "try_this_for_a_week"];
  const visibleKeys = orderedKeys.filter((key, index, list) => sections[key] && list.indexOf(key) === index).slice(0, 6);
  const primary = sections.what_to_change_next_week || sections.next_weeks_play || sections.what_to_change_next_month || sections.suggested_adjustment;
  return (
    <Stack spacing={2.2}>
      <IntelHero
        eyebrow={String(brief.period?.label || horizon).toUpperCase()}
        title={brief.headline?.title || "TrustBud is reading the pattern."}
        body={brief.headline?.body || "Keep checking in so TrustBud can learn what works over time."}
      />
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" }, gap: 1 }}>
        <MetricTile label="Done" value={brief.metrics?.habit_days_moved ?? brief.metrics?.habits_moved ?? 0} />
        <MetricTile label="Slipped" value={brief.metrics?.habit_days_slipped ?? brief.metrics?.habits_slipped ?? 0} />
        <MetricTile label="Kept alive" value={brief.metrics?.small_versions_used ?? 0} />
        <MetricTile label="Recoveries" value={brief.metrics?.recoveries ?? 0} />
      </Box>
      {primary ? (
        <BriefSection title={primary.title || "What to change next"} body={primary.body || primary.hypothesis || ""}>
          {primary.evidence?.length ? (
            <InsightDetailRow label="Evidence">
              <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                {evidenceItems(primary.evidence).slice(0, 3).map((item, index) => (
                  <Chip key={`${item.value}-${index}`} size="small" label={`${item.label}: ${item.value}`} />
                ))}
              </Stack>
            </InsightDetailRow>
          ) : null}
          {primary.watching_label || primary.watching ? (
            <InsightDetailRow label="Watching">
              {cleanCopy(primary.watching_label || primary.watching)}
            </InsightDetailRow>
          ) : null}
          <InsightDetailRow label="Action">
            <Button variant="contained" sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 900 }} onClick={() => onPrimary(primary)}>
              {cleanCopy(primary.primary_cta || primary.cta || "Open Today")}
            </Button>
          </InsightDetailRow>
        </BriefSection>
      ) : null}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
        {visibleKeys.map((key) => (
          <PeriodSection key={key} sectionKey={key} section={sections[key]} />
        ))}
      </Box>
    </Stack>
  );
}

function PeriodSection({ sectionKey, section }) {
  if (!section) return null;
  if (Array.isArray(section.items)) {
    if (!section.items.length) return null;
    return (
      <BriefSection title={section.title || titleFromKey(sectionKey)} body="">
        <Stack spacing={1.1} sx={{ mt: 1.2 }}>
          {section.items.slice(0, 4).map((item, index) => (
            <Box key={`${item.title}-${index}`} sx={{ borderTop: index ? "1px solid #eef3f8" : "none", pt: index ? 1 : 0 }}>
              {statusLabel(item.status, item.status_label) ? <Chip size="small" label={statusLabel(item.status, item.status_label)} sx={{ mb: 0.8, fontWeight: 850 }} /> : null}
              <Typography sx={{ color: "#13233d", fontWeight: 900 }}>{cleanCopy(item.title)}</Typography>
              <Typography sx={{ mt: 0.35, color: "#56667a", fontSize: 14 }}>{cleanCopy(item.body)}</Typography>
            </Box>
          ))}
        </Stack>
      </BriefSection>
    );
  }
  if (sectionKey === "goal_momentum") {
    const moving = Array.isArray(section.moving) ? section.moving : [];
    const quiet = Array.isArray(section.going_quiet) ? section.going_quiet : [];
    if (!moving.length && !quiet.length) return null;
    return (
      <BriefSection title="Goal momentum" body="">
        {[...moving.slice(0, 2), ...quiet.slice(0, 2)].map((item, index) => (
          <Box key={`${item.title}-${index}`} sx={{ mt: 1 }}>
            <Typography sx={{ color: "#13233d", fontWeight: 900 }}>{cleanCopy(item.title)}</Typography>
            <Typography sx={{ color: "#56667a", fontSize: 14 }}>{cleanCopy(item.body)}</Typography>
          </Box>
        ))}
      </BriefSection>
    );
  }
  const body = cleanCopy(section.body || section.hypothesis || section.experiment || "");
  if (!body) return null;
  return <BriefSection title={section.title || titleFromKey(sectionKey)} body={body} />;
}

function PlanHealthInsightsTab({ payload, busy, onReview, onOpenPlan, onAskPrompt }) {
  if (payload?.locked) {
    return (
      <BriefSection
        title="Unlock Plan Health"
        body="Plus checks whether your plan is light enough, clear enough, and connected to habits you can actually follow."
      >
        <Button variant="contained" sx={{ mt: 1.4, borderRadius: "8px", textTransform: "none", fontWeight: 900 }} onClick={onOpenPlan}>
          Open Plan
        </Button>
      </BriefSection>
    );
  }

  const review = payload?.review || {};
  const biggestRisk = review.biggest_consistency_risk || null;
  const biggestRiskTitle = cleanCopy(biggestRisk?.title || "");
  const biggestRiskClaim = cleanCopy(biggestRisk?.claim || "");
  const diagnosis = review.plan_diagnosis?.body
    ? review.plan_diagnosis
    : review.headline?.title
      ? { title: review.headline.title, body: review.headline.body || "" }
      : null;
  const suggestions = Array.isArray(review.suggestions) ? review.suggestions : [];
  const topChange = Array.isArray(review.top_changes) && review.top_changes.length ? review.top_changes[0] : null;
  const matchedSuggestion = topChange?.suggestion_id
    ? suggestions.find((item) => String(item.id) === String(topChange.suggestion_id))
    : suggestions[0] || null;
  const healthDimensions = Array.isArray(review.health_dimensions) ? review.health_dimensions : [];
  const attention = healthDimensions.find((item) => item?.status === "needs_attention") || healthDimensions[0] || null;
  const keepSteady = Array.isArray(review.what_not_to_change)
    ? review.what_not_to_change.find((item) => item?.title || item?.body)
    : null;
  const lastAdjustment = review.last_adjustment_result || null;
  const changeTitle = cleanCopy(topChange?.title || matchedSuggestion?.title || "Review the next plan adjustment");
  const changeReason = cleanCopy(topChange?.reason || matchedSuggestion?.why_it_matters || matchedSuggestion?.body || "");
  const changeCta = cleanCopy(topChange?.primary_cta || matchedSuggestion?.primary_cta || "Open Plan Health");
  const before = cleanCopy(topChange?.before || matchedSuggestion?.proposed_change?.current_value || "Current setup");
  const after = cleanCopy(topChange?.after || matchedSuggestion?.proposed_change?.proposed_value || changeCta);
  const hasReview = Boolean(payload?.id || biggestRiskTitle || biggestRiskClaim || diagnosis?.title || topChange || matchedSuggestion || healthDimensions.length);

  if (!hasReview) {
    return (
      <Stack spacing={2.2}>
        <IntelHero
          eyebrow="Plan Health"
          title="Check whether the plan is followable."
          body="TrustBud can look across goals, habits, cadence, timing, and what should stay unchanged before you edit more."
          icon={<InsightsRoundedIcon />}
        />
        <BriefSection title="No Plan Health check yet" body={review.learning_state?.body || "Run a Plan Health check to get one diagnosis and one practical next change."}>
          <Stack direction="row" spacing={1} sx={{ mt: 1.4, flexWrap: "wrap" }}>
            <Button variant="contained" disabled={busy} onClick={onReview} sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 900 }}>
              {busy ? "Checking..." : "Check Plan Health"}
            </Button>
            <Button variant="outlined" onClick={onOpenPlan} sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 900 }}>
              Open Plan
            </Button>
          </Stack>
        </BriefSection>
      </Stack>
    );
  }

  return (
    <Stack spacing={2.2}>
      <IntelHero
        eyebrow="Plan Health"
        title={biggestRiskTitle || diagnosis?.title || review.headline?.title || "Plan Health"}
        body={biggestRiskClaim || diagnosis?.body || review.headline?.body || "TrustBud checked whether your plan is realistic enough to follow."}
        icon={<InsightsRoundedIcon />}
      />

      <ConsistencyRiskPanel
        risk={biggestRisk}
        actionFallback={changeCta || "Open Plan Health"}
        onAction={onOpenPlan}
        onAsk={(riskItem) => onAskPrompt(`Why is this the biggest consistency risk? ${riskItem.claim || riskItem.title || ""}`)}
      />

      {(topChange || matchedSuggestion) ? (
        <BriefSection title="Change this" body={changeReason || "This is the clearest plan adjustment to review first."}>
          <Box sx={{ mt: 1.2, display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1 }}>
            <PlanHealthDiff label="Before" value={before} />
            <PlanHealthDiff label="After" value={after} />
          </Box>
          <Stack direction="row" spacing={1} sx={{ mt: 1.4, flexWrap: "wrap" }}>
            <Button variant="contained" onClick={onOpenPlan} sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 900 }}>
              {changeCta}
            </Button>
            <Button
              variant="outlined"
              onClick={() => onAskPrompt(`Explain this Plan Health change and the evidence behind it: ${changeTitle}. ${changeReason}`)}
              sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 900 }}
            >
              Ask about this
            </Button>
          </Stack>
        </BriefSection>
      ) : null}

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
        {attention ? (
          <BriefSection title="What TrustBud checked" body={attention.summary || "TrustBud checked the plan for load, timing, clarity, recovery, and goal alignment."}>
            {Array.isArray(attention.evidence) && attention.evidence.length ? (
              <Stack direction="row" spacing={1} sx={{ mt: 1.1, flexWrap: "wrap" }}>
                {attention.evidence.slice(0, 3).map((item, index) => (
                  <Chip key={`${item}-${index}`} size="small" label={cleanCopy(item)} />
                ))}
              </Stack>
            ) : null}
          </BriefSection>
        ) : null}

        {keepSteady ? (
          <BriefSection title="Keep this" body={`${cleanCopy(keepSteady.title || "")}${keepSteady.body ? ` ${cleanCopy(keepSteady.body)}` : ""}`} />
        ) : null}

        {lastAdjustment ? (
          <BriefSection title="What TrustBud will watch" body={cleanCopy(lastAdjustment.body || lastAdjustment.title || "TrustBud is watching whether the last adjustment helped.")}>
            {lastAdjustment.status ? <Chip size="small" label={lastAdjustmentStatusText(lastAdjustment.status)} sx={{ mt: 1, fontWeight: 850 }} /> : null}
          </BriefSection>
        ) : null}
      </Box>

      <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
        <Button variant="outlined" disabled={busy} onClick={onReview} sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 900 }}>
          {busy ? "Checking..." : "Check again"}
        </Button>
        <Button variant="text" onClick={onOpenPlan} sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 900 }}>
          Open full Plan Check
        </Button>
      </Stack>
    </Stack>
  );
}

function PlanHealthDiff({ label, value }) {
  return (
    <Paper elevation={0} sx={{ p: 1.3, borderRadius: "8px", border: "1px solid #e4ebf3", bgcolor: "#f8fafc" }}>
      <Typography sx={{ color: "#64748b", fontSize: 12, fontWeight: 950, textTransform: "uppercase" }}>{label}</Typography>
      <Typography sx={{ mt: 0.45, color: "#13233d", fontWeight: 850 }}>{value || "No change"}</Typography>
    </Paper>
  );
}

function lastAdjustmentStatusText(status) {
  return {
    watching: "Watching",
    helped: "Helped",
    not_helping_yet: "Not helping yet",
    too_early: "Too early",
    no_data: "Still learning",
  }[status] || "Watching";
}

function LearnedTab({ payload, busy, onMemoryAction }) {
  const memories = Array.isArray(payload?.memories) ? payload.memories : [];
  const stoppedStatuses = new Set(["dismissed", "stopped", "not_true", "not_working"]);
  const confirmed = memories.filter((item) => item.status === "confirmed");
  const stopped = memories.filter((item) => stoppedStatuses.has(String(item.status || "")));
  const learning = memories.filter((item) => item.status !== "confirmed" && !stoppedStatuses.has(String(item.status || "")));
  if (payload?.locked) {
    return (
      <BriefSection
        title="What TrustBud knows"
        body="Plus unlocks the personal operating manual TrustBud builds from your goals, habits, timing, and Accountability."
      />
    );
  }
  return (
    <Stack spacing={2.2}>
      <IntelHero
        eyebrow="What works for you"
        title="What TrustBud is learning"
        body="Confirm what fits. Correct what does not. TrustBud uses this in Today, Plan, Accountability, and coach communications."
        icon={<PsychologyRoundedIcon />}
      />
      <LearningList title="Confirmed" empty="No confirmed patterns yet." memories={confirmed} busy={busy} onMemoryAction={onMemoryAction} />
      <LearningList title="Still learning" empty="TrustBud is still collecting enough signal." memories={learning} busy={busy} onMemoryAction={onMemoryAction} />
      <LearningList title="Stopped / Not true" empty="Nothing has been stopped yet." memories={stopped} busy={busy} onMemoryAction={onMemoryAction} />
    </Stack>
  );
}

function LearningList({ title, empty, memories, busy, onMemoryAction }) {
  return (
    <BriefSection title={title} body="">
      {memories.length ? (
        <Stack spacing={1.2} sx={{ mt: 1.2 }}>
          {memories.slice(0, 6).map((memory) => {
            const memoryId = Number(memory.id || 0);
            const stopped = ["dismissed", "stopped", "not_true", "not_working"].includes(String(memory.status || ""));
            const evidence = evidenceItems(memory.evidence);
            return (
              <Box key={`${memory.id}-${memory.title || memory.summary}`} sx={{ p: 1.25, borderRadius: "8px", bgcolor: "#f8fafc", border: "1px solid #e8eef5" }}>
                {statusLabel(memory.outcome_status || memory.status, memory.status_label) ? (
                  <Chip size="small" label={statusLabel(memory.outcome_status || memory.status, memory.status_label)} sx={{ mb: 0.8, fontWeight: 850 }} />
                ) : null}
                <Typography sx={{ color: "#13233d", fontWeight: 900 }}>{cleanCopy(memory.title || "What TrustBud is learning")}</Typography>
                <Typography sx={{ mt: 0.35, color: "#56667a", fontSize: 14 }}>{cleanCopy(memory.summary || memory.body || "")}</Typography>
                {evidence.length ? (
                  <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
                    {evidence.slice(0, 3).map((item, index) => <Chip key={`${item.value}-${index}`} size="small" label={`${item.label}: ${item.value}`} />)}
                  </Stack>
                ) : null}
                {memory.used_in?.length ? <Typography sx={{ mt: 0.8, color: "#64748b", fontSize: 13 }}>Used in: {formatUsedIn(memory.used_in)}</Typography> : null}
                {memoryId ? (
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                    <Button size="small" variant="outlined" disabled={busy} onClick={() => onMemoryAction(memoryId, "keep")}>
                      {stopped ? "Use again" : "True"}
                    </Button>
                    {!stopped ? (
                      <Button size="small" variant="outlined" disabled={busy} onClick={() => onMemoryAction(memoryId, "not_true")}>
                        Not true
                      </Button>
                    ) : null}
                  </Stack>
                ) : null}
              </Box>
            );
          })}
        </Stack>
      ) : (
        <Typography sx={{ mt: 1, color: "#66758b" }}>{empty}</Typography>
      )}
    </BriefSection>
  );
}

function AskTrustBudTab({ question, answer, busy, examples, onQuestionChange, onSubmit, onAction, onFeedback }) {
  const [showReasons, setShowReasons] = useState(false);
  useEffect(() => {
    setShowReasons(false);
  }, [answer?.action_event_id, answer?.answer_title]);
  return (
    <Stack spacing={2.2}>
      <IntelHero
        eyebrow="Ask TrustBud"
        title="Ask about your follow-through"
        body="TrustBud answers from your goals, habits, timing, Accountability, and what it has already learned."
        icon={<ChatBubbleOutlineRoundedIcon />}
      />
      <Paper elevation={0} sx={{ p: 2.1, borderRadius: "8px", border: "1px solid #e6edf5", bgcolor: "#fff" }}>
        <TextField
          fullWidth
          multiline
          minRows={3}
          value={question}
          onChange={(event) => onQuestionChange(event.target.value)}
          placeholder="Ask about a habit, goal, timing, recovery, or Accountability"
        />
        <Button variant="contained" disabled={!question.trim() || busy} onClick={onSubmit} sx={{ mt: 1.3, borderRadius: "8px", textTransform: "none", fontWeight: 900 }}>
          {busy ? "Asking..." : "Ask TrustBud"}
        </Button>
        {examples.length ? (
          <Stack direction="row" spacing={1} sx={{ mt: 1.3, flexWrap: "wrap" }}>
            {examples.map((example) => (
              <Button key={example} size="small" variant="outlined" onClick={() => onQuestionChange(example)} sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 850 }}>
                {example}
              </Button>
            ))}
          </Stack>
        ) : null}
      </Paper>
      {answer ? (
        <BriefSection title={answer.answer_title || "TrustBud answered"} body={answer.answer_body || ""}>
          {Array.isArray(answer.evidence) && answer.evidence.length ? (
            <Stack direction="row" spacing={1} sx={{ mt: 1.2, flexWrap: "wrap" }}>
              {evidenceItems(answer.evidence).slice(0, 4).map((item, index) => <Chip key={`${item.value}-${index}`} size="small" label={`${item.label}: ${item.value}`} />)}
            </Stack>
          ) : null}
          {answer.learning_state ? (
            <Paper elevation={0} sx={{ mt: 1.2, p: 1.4, borderRadius: "8px", bgcolor: "#f8fafc", border: "1px solid #e2e8f0" }}>
              <Typography sx={{ color: "#13233d", fontWeight: 900 }}>{cleanCopy(answer.learning_state.title || "TrustBud is still learning.")}</Typography>
              <Typography sx={{ mt: 0.4, color: "#56667a", fontSize: 14 }}>{cleanCopy(answer.learning_state.body || "")}</Typography>
            </Paper>
          ) : null}
          {answer.suggested_action ? (
            <Button variant="contained" sx={{ mt: 1.2, borderRadius: "8px", textTransform: "none", fontWeight: 900 }} disabled={busy} onClick={() => onAction(answer.suggested_action)}>
              {cleanCopy(answer.suggested_action.cta || "Open Today")}
            </Button>
          ) : null}
          <Stack direction="row" spacing={1} sx={{ mt: 1.2, flexWrap: "wrap" }}>
            <Button size="small" variant="outlined" disabled={busy} onClick={() => onFeedback("helpful")}>Helpful</Button>
            <Button size="small" variant="outlined" disabled={busy} onClick={() => setShowReasons((current) => !current)}>Not helpful</Button>
          </Stack>
          {showReasons ? (
            <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
              {ASK_NOT_HELPFUL_REASONS.map((reason) => (
                <Button key={reason.value} size="small" variant="outlined" disabled={busy} onClick={() => onFeedback("not_helpful", reason.value)}>
                  {reason.label}
                </Button>
              ))}
            </Stack>
          ) : null}
        </BriefSection>
      ) : null}
    </Stack>
  );
}

function buildAskPrompts({ personalIntel, brief, todayPlanState }) {
  const prompts = [];
  const firstOpen = (todayPlanState || []).find((item) => item?.title && !["done", "backup_done", "small_version_done", "completed"].includes(String(item.status_today || "")));
  if (firstOpen?.title) prompts.push(`What would make ${firstOpen.title} easier today?`);
  if (personalIntel?.suggested_adjustment?.title) prompts.push("Why is this adjustment the right one?");
  if (brief?.strategy?.title) prompts.push("What should I focus on first?");
  prompts.push("What pattern is TrustBud most confident about?");
  return [...new Set(prompts)].slice(0, 4);
}

export default function IntelligencePage() {
  const navigate = useNavigate();
  const [horizon, setHorizon] = useState("analytics");
  const [payload, setPayload] = useState(null);
  const [learnedPayload, setLearnedPayload] = useState(null);
  const [analyticsPayload, setAnalyticsPayload] = useState(null);
  const [planReviewPayload, setPlanReviewPayload] = useState(null);
  const [analyticsDays, setAnalyticsDays] = useState(90);
  const [askQuestion, setAskQuestion] = useState("");
  const [askAnswer, setAskAnswer] = useState(null);
  const [askBusy, setAskBusy] = useState(false);
  const [planReviewBusy, setPlanReviewBusy] = useState(false);
  const [memoryBusy, setMemoryBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingPhase, setLoadingPhase] = useState("summary");
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [applying, setApplying] = useState(false);
  const [busyChangeId, setBusyChangeId] = useState(null);

  const load = useCallback(async (mode = "initial") => {
    setError("");
    setLoadingPhase(mode === "refresh" ? "refreshing" : "summary");
    try {
      if (horizon === "knows") {
        setPayload(null);
        setAnalyticsPayload(null);
        setPlanReviewPayload(null);
        setLearnedPayload(await fetchLearnedIntelligence());
        return;
      }
      if (horizon === "ask") {
        setAnalyticsPayload(null);
        setLearnedPayload(null);
        setPlanReviewPayload(null);
        return;
      }
      if (horizon === "plan_health") {
        setPayload(null);
        setAnalyticsPayload(null);
        setLearnedPayload(null);
        setPlanReviewPayload(await fetchPlanReview({ scope: "whole_plan" }));
        return;
      }
      if (horizon === "analytics") {
        const [summary, learned, analytics] = await Promise.all([
          fetchIntelligenceSummary("daily"),
          fetchLearnedIntelligence().catch(() => null),
          fetchIntelligenceAnalytics(analyticsDays),
        ]);
        setPayload(summary || null);
        setLearnedPayload(learned || null);
        setAnalyticsPayload(analytics || null);
        setPlanReviewPayload(null);
        return;
      }

      const data = await fetchIntelligenceSummary(horizon);
      setLearnedPayload(null);
      setAnalyticsPayload(null);
      setPlanReviewPayload(null);
      if (horizon === "daily" && data?.is_plus && !data?.locked && data?.daily_brief?.source !== "ai_cached" && !data?.daily_brief?.ai_unavailable) {
        setPayload(data || null);
        setLoadingPhase("daily_brief");
        const ensured = await ensureDailyIntelligenceBrief(mode === "refresh" ? "manual_refresh" : "first_open", { force: mode === "refresh" });
        setPayload(ensured || data || null);
      } else if (["weekly", "monthly"].includes(horizon) && data?.is_plus && !data?.locked && data?.period_brief?.source !== "ai_cached" && data?.can_ensure_ai_brief) {
        setPayload(data || null);
        setLoadingPhase("period_brief");
        const ensured = await ensureHorizonIntelligenceBrief(horizon, {
          triggerReason: mode === "refresh" ? "manual_refresh" : "first_open",
          force: mode === "refresh",
        });
        setPayload(ensured || data || null);
      } else {
        setPayload(data || null);
      }
    } catch (err) {
      setError(friendlyInsightsError(err?.message));
    } finally {
      setLoading(false);
    }
  }, [analyticsDays, horizon]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    const timeoutId = window.setTimeout(() => {
      if (!active) return;
      setLoading(false);
      setError((current) => current || INSIGHTS_LOAD_STUCK_MESSAGE);
    }, INSIGHTS_LOAD_TIMEOUT_MS);
    load().finally(() => {
      window.clearTimeout(timeoutId);
    });
    return () => {
      active = false;
      window.clearTimeout(timeoutId);
    };
  }, [load]);

  const risk = payload?.biggest_risk || null;
  const consistencyRisk = payload?.biggest_consistency_risk || null;
  const fix = payload?.recommended_fix || null;
  const dailyBrief = payload?.daily_brief || null;
  const workingItems = useMemo(
    () => [
      ...(Array.isArray(payload?.what_is_working) ? payload.what_is_working : []),
      ...(Array.isArray(payload?.patterns) ? payload.patterns.slice(0, 1) : []),
    ].slice(0, 3),
    [payload]
  );
  const askExamples = useMemo(
    () =>
      buildAskPrompts({
        personalIntel: payload?.personal_intelligence,
        brief: dailyBrief,
        todayPlanState: payload?.today_plan_state,
      }),
    [dailyBrief, payload]
  );

  async function handlePlanHealthReview({ forceRefresh = false } = {}) {
    setPlanReviewBusy(true);
    setNotice("");
    setError("");
    try {
      const result = await ensurePlanReview({
        allowLiveAi: true,
        forceRefresh,
        scope: "whole_plan",
      });
      setPlanReviewPayload(result || null);
      setNotice("Plan Health updated.");
    } catch (err) {
      setError(err?.message || "Unable to update Plan Health.");
    } finally {
      setPlanReviewBusy(false);
    }
  }

  async function handleApply(intent = "make_easier") {
    setApplying(true);
    setNotice("");
    try {
      const result = await applyIntelligenceRecommendation({
        insight: risk || fix || {},
        intent,
        commitmentId: risk?.commitment_id || fix?.commitment_id || "",
      });
      setNotice(result?.message || "Plan updated.");
      await load();
    } catch (err) {
      setError(err?.message || "Unable to update the plan.");
    } finally {
      setApplying(false);
    }
  }

  async function handleIntelAction(strategy = {}) {
    const action = strategy?.strategy_action || {};
    const deeplink = action.deeplink || strategy.deeplink || "";
    if (deeplink) {
      navigate(deeplink);
      return;
    }
    if (["focus_habit", "protect_habit"].includes(strategy.action_type)) {
      navigate(strategy.commitment_id ? `/plan?actionId=${strategy.commitment_id}` : "/plan");
      return;
    }
    navigate(strategy.commitment_id ? `/?commitmentId=${strategy.commitment_id}` : "/");
  }

  async function handleUndo(changeId) {
    setBusyChangeId(changeId);
    setNotice("");
    try {
      const result = await updateAppliedIntelligenceChange({ action: "undo", changeId });
      setNotice(result?.message || "Change undone.");
      await load();
    } catch (err) {
      setError(err?.message || "Unable to undo this change.");
    } finally {
      setBusyChangeId(null);
    }
  }

  async function handleFeedback(changeId, feedback) {
    setBusyChangeId(changeId);
    try {
      await updateAppliedIntelligenceChange({ action: "feedback", changeId, feedback });
      await load();
    } catch (err) {
      setError(err?.message || "Unable to save feedback.");
    } finally {
      setBusyChangeId(null);
    }
  }

  function handleViewPlan(change) {
    const actionId = change?.commitment?.id;
    navigate(actionId ? `/plan?actionId=${actionId}` : "/plan");
  }

  async function handleMemoryAction(memoryId, action) {
    setMemoryBusy(true);
    setNotice("");
    try {
      const result = await updatePersonalMemory(memoryId, action);
      setNotice(result?.effect?.message || "Learning updated.");
      setLearnedPayload(await fetchLearnedIntelligence());
    } catch (err) {
      setError(err?.message || "Unable to update this learning.");
    } finally {
      setMemoryBusy(false);
    }
  }

  async function handleAskSubmit() {
    if (!askQuestion.trim()) return;
    setAskBusy(true);
    setError("");
    try {
      const result = await askTrustBud(askQuestion.trim());
      setAskAnswer(result || null);
    } catch (err) {
      setError(err?.message || "Unable to ask TrustBud.");
    } finally {
      setAskBusy(false);
    }
  }

  async function handleAskFeedback(interaction, reason = "") {
    if (!askAnswer?.action_event_id) return;
    setAskBusy(true);
    try {
      await recordIntelligenceAction({
        actionEventId: askAnswer.action_event_id,
        interaction,
        reason,
      });
      setNotice(interaction === "helpful" ? "Feedback saved." : "Feedback saved. TrustBud will adjust.");
    } catch (err) {
      setError(err?.message || "Unable to save feedback.");
    } finally {
      setAskBusy(false);
    }
  }

  function handleAskPrompt(prompt) {
    setHorizon("ask");
    setAskQuestion(prompt);
  }

  if (loading) {
    const loadingTitle =
      loadingPhase === "daily_brief"
        ? "Building today’s Insights"
        : loadingPhase === "period_brief"
          ? `Building this ${horizon === "monthly" ? "month" : "week"}`
          : horizon === "analytics"
            ? "Preparing Insights"
            : horizon === "plan_health"
              ? "Checking Plan Health"
            : horizon === "knows"
              ? "Reading what TrustBud knows"
              : "Reading today’s plan";
    return (
      <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 820, mx: "auto" }}>
        <PendingActionNotice
          title={loadingTitle}
          body={
            loadingPhase === "daily_brief"
              ? "TrustBud is checking the whole plan, saved guidance, and what changed today. When it finishes, the page will open faster next time."
              : horizon === "analytics"
                ? "TrustBud is checking your habits, recent reports, saved patterns, and plan signals."
                : horizon === "plan_health"
                  ? "TrustBud is checking plan load, timing, cadence, and what should stay steady."
                : "TrustBud is checking habits and saved Insights before showing the page."
          }
          steps={
            loadingPhase === "daily_brief"
              ? ["Reading the plan", "Checking recent activity", "Saving the finished brief"]
              : horizon === "analytics"
                ? ["Reading recent activity", "Checking saved patterns", "Preparing the page"]
                : horizon === "plan_health"
                  ? ["Reading goals and habits", "Checking plan load", "Preparing Plan Health"]
                : ["Checking Today", "Looking for saved guidance", "Preparing the page"]
          }
        />
      </Box>
    );
  }

  if (payload?.locked || learnedPayload?.locked || analyticsPayload?.locked || planReviewPayload?.locked) {
    const lockedPayload = payload || learnedPayload || analyticsPayload || planReviewPayload || {};
    const lockedTitle = usefulLockedCopy(
      lockedPayload.headline?.title,
      "Unlock personal Insights",
      [/^trustbud intelligence$/i, /^unlock insights$/i]
    );
    const lockedBody = usefulLockedCopy(
      lockedPayload.headline?.subtitle || lockedPayload.detail,
      "TrustBud turns your real habit data into clearer next moves, plan adjustments, and questions you can ask about your own patterns.",
      [/^today.?s move$/i, /^insights$/i]
    );
    const lockedCards = [
      {
        icon: <BarChartRoundedIcon />,
        title: "Patterns",
        body: "See when your follow-through is strongest and which habits are starting to drift.",
      },
      {
        icon: <CheckCircleRoundedIcon />,
        title: "Plan fixes",
        body: "Turn broad goals into smaller timing and accountability changes you can actually use.",
      },
      {
        icon: <ChatBubbleOutlineRoundedIcon />,
        title: "Ask TrustBud",
        body: "Ask about your habits, goals, timing, recovery, and accountability history.",
      },
    ];
    return (
      <Box sx={{ maxWidth: 1120, mx: "auto", py: 3, px: { xs: 2, md: 4 } }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.4, md: 3.4 },
            borderRadius: "12px",
            border: "1px solid rgba(210, 226, 243, 0.95)",
            overflow: "hidden",
            position: "relative",
            background:
              "radial-gradient(circle at top right, rgba(78, 169, 235, 0.22), transparent 32%), linear-gradient(135deg, #ffffff 0%, #f4f8ff 100%)",
            boxShadow: "0 22px 60px rgba(19, 35, 61, 0.1)",
          }}
        >
          <Box sx={{ maxWidth: 760 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 850, color: "#2f5d8f", textTransform: "uppercase", letterSpacing: 0.6 }}>
              TrustBud Plus
            </Typography>
            <Typography variant="h4" sx={{ mt: 1, fontWeight: 950, color: "#13233d", letterSpacing: 0, lineHeight: 1.12 }}>
              {lockedTitle}
            </Typography>
            <Typography sx={{ mt: 1.2, color: "#44566f", fontSize: 17, lineHeight: 1.65 }}>
              {lockedBody}
            </Typography>
          </Box>
          <Box
            sx={{
              mt: 2.4,
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
              gap: 1.35,
            }}
          >
            {lockedCards.map((card) => (
              <Box
                key={card.title}
                sx={{
                  p: 1.55,
                  borderRadius: "10px",
                  border: "1px solid rgba(211, 225, 240, 0.95)",
                  backgroundColor: "rgba(255, 255, 255, 0.8)",
                }}
              >
                <Box sx={{ color: "#2f5d8f", display: "inline-flex" }}>{card.icon}</Box>
                <Typography sx={{ mt: 0.8, color: "#13233d", fontWeight: 900 }}>{card.title}</Typography>
                <Typography sx={{ mt: 0.45, color: "#5b6f89", lineHeight: 1.45, fontSize: 14 }}>
                  {card.body}
                </Typography>
              </Box>
            ))}
          </Box>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.1} sx={{ mt: 2.4 }}>
            <Button
              variant="contained"
              sx={{ borderRadius: "8px", minHeight: 44, px: 2.4, textTransform: "none", fontWeight: 900 }}
              onClick={() => navigate("/trustbud-plus?feature=intelligence")}
            >
              Unlock TrustBud Plus
            </Button>
            <Button
              variant="outlined"
              sx={{ borderRadius: "8px", minHeight: 44, px: 2.4, textTransform: "none", fontWeight: 850 }}
              onClick={() => navigate("/today")}
            >
              Back to Today
            </Button>
          </Stack>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1120, mx: "auto", py: 3, px: { xs: 2, md: 4 } }}>
      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
      {notice ? <Alert severity="success" sx={{ mb: 2 }}>{notice}</Alert> : null}
      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: "stretch", md: "center" }} sx={{ mb: 2 }}>
        <HorizonSwitch active={horizon} onChange={setHorizon} />
        {!["ask", "knows"].includes(horizon) ? (
          <Button
            variant="outlined"
            onClick={() => {
              if (horizon === "plan_health") {
                handlePlanHealthReview({ forceRefresh: true });
              } else {
                load("refresh");
              }
            }}
            disabled={planReviewBusy}
            sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 900 }}
          >
            {horizon === "plan_health" ? (planReviewBusy ? "Checking..." : "Check again") : "Refresh Insights"}
          </Button>
        ) : null}
      </Stack>

      {horizon === "analytics" ? (
        <AnalyticsDataRoom
          payload={payload}
          analytics={analyticsPayload}
          learnedPayload={learnedPayload}
          days={analyticsDays}
          onDaysChange={setAnalyticsDays}
          onAskPrompt={handleAskPrompt}
        />
      ) : horizon === "plan_health" ? (
        <PlanHealthInsightsTab
          payload={planReviewPayload}
          busy={planReviewBusy}
          onReview={() => handlePlanHealthReview({ forceRefresh: true })}
          onOpenPlan={() => navigate("/plan?source=insights&review=plan_health")}
          onAskPrompt={handleAskPrompt}
        />
      ) : horizon === "weekly" || horizon === "monthly" ? (
        <PeriodBriefView brief={payload?.period_brief} onPrimary={handleIntelAction} />
      ) : horizon === "knows" ? (
        <LearnedTab payload={learnedPayload} busy={memoryBusy} onMemoryAction={handleMemoryAction} />
      ) : horizon === "ask" ? (
        <AskTrustBudTab
          question={askQuestion}
          answer={askAnswer}
          busy={askBusy}
          examples={askExamples}
          onQuestionChange={setAskQuestion}
          onSubmit={handleAskSubmit}
          onAction={handleIntelAction}
          onFeedback={handleAskFeedback}
        />
      ) : (
        <>
          <DailyBriefCard brief={dailyBrief} personalIntel={payload?.personal_intelligence} onStrategy={handleIntelAction} applying={applying} />
          <Box sx={{ mt: 2.4 }}>
            <ConsistencyRiskPanel
              risk={consistencyRisk}
              actionFallback="Open Today"
              onAction={(riskItem) => {
                const action = riskItem?.recommended_next_action || {};
                handleIntelAction({
                  action_type: action.action || action.type || "open_today",
                  deeplink: action.deeplink || "",
                  strategy_action: action,
                });
              }}
              onAsk={(riskItem) => handleAskPrompt(`Why is this the biggest consistency risk? ${riskItem.claim || riskItem.title || ""}`)}
            />
          </Box>

          <Box sx={{ mt: 2.6, display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1.1fr 0.9fr" }, gap: 2.4 }}>
            <Stack spacing={2.4}>
              {!dailyBrief ? (
                <>
                  <Box>
                    <SectionTitle title="Pattern" kicker="The pattern to fix first" />
                    <Box sx={{ mt: 1.1 }}>
                      <InsightCard
                        icon={<InsightsRoundedIcon />}
                        title={getInsightTitle(risk, "No major pattern yet")}
                        body={getInsightBody(risk, "Keep collecting check-ins and completions so TrustBud can find a reliable pattern.")}
                      />
                    </Box>
                  </Box>

                  <Box>
                    <SectionTitle title="Next Useful Change" kicker="Make the next attempt easier" />
                    <Box sx={{ mt: 1.1 }}>
                      <InsightCard
                        icon={<CheckCircleRoundedIcon />}
                        title={getInsightTitle(fix, "Make the next attempt easier")}
                        body={getInsightBody(fix, "Add a keep-alive step so this habit is not all-or-nothing.")}
                      >
                        <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                          <Button
                            variant="contained"
                            disabled={applying}
                            onClick={() => handleApply("make_easier")}
                          >
                            {applying ? "Applying..." : getActionLabel(fix, "Add keep-alive step")}
                          </Button>
                          <Button variant="text" onClick={() => navigate("/plan")}>
                            Open Plan
                          </Button>
                        </Stack>
                      </InsightCard>
                    </Box>
                  </Box>
                </>
              ) : null}
            </Stack>

            <Stack spacing={2.4}>
              <AppliedChangesCard
                changes={payload?.applied_changes || []}
                busyId={busyChangeId}
                onUndo={handleUndo}
                onFeedback={handleFeedback}
                onViewPlan={handleViewPlan}
              />

              <Paper elevation={0} sx={{ p: 2.1, borderRadius: "8px", border: "1px solid #e6edf5", bgcolor: "#ffffff" }}>
                <Typography sx={{ fontSize: 18, fontWeight: 850, color: "#13233d" }}>
                  What is working
                </Typography>
                <Stack spacing={1.4} sx={{ mt: 1.5 }}>
                  {workingItems.length ? workingItems.map((item, index) => (
                    <Box key={`${item.type || "working"}-${index}`} sx={{ borderTop: index ? "1px solid #eef3f8" : "none", pt: index ? 1.3 : 0 }}>
                      <Typography sx={{ fontWeight: 800, color: "#13233d" }}>
                        {getInsightTitle(item, "Insight")}
                      </Typography>
                      <Typography sx={{ mt: 0.35, color: "#44566f", fontSize: 14 }}>
                        {getInsightBody(item, "This pattern is still developing.")}
                      </Typography>
                    </Box>
                  )) : (
                    <Typography sx={{ color: "#66758b" }}>
                      TrustBud needs a little more data before it can call out what is working.
                    </Typography>
                  )}
                </Stack>
              </Paper>
            </Stack>
          </Box>
        </>
      )}
    </Box>
  );
}
